import { useCallback } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import * as z from 'zod'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form/form-field.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { useAccount } from '@/modules/accounts/hooks/useAccounts.ts'
import { useDisableTfa } from '@/modules/profile-settings/hooks/useDisableTfa.ts'

interface Enable2faPopupProps {
  show: boolean
  onClose: () => void
  onSuccess: () => void
}

export type DisableTfaForm = z.infer<typeof disableTfaFormValues>

const disableTfaFormValues = z.object({
  authCode: z.string(),
})

export const DisableTfaDialog = ({
  show,
  onClose,
  onSuccess,
}: Enable2faPopupProps) => {
  const disableTfaMutation = useDisableTfa()
  const form = useForm<DisableTfaForm>({
    mode: 'all',
    resolver: zodResolver(disableTfaFormValues),
    defaultValues: {
      authCode: '',
    },
  })
  const { setTfaEnabled } = useAccount()
  const toast = useToast()

  const { handleSubmit } = form

  const onSubmit = useCallback(
    (values: DisableTfaForm) => {
      disableTfaMutation
        .mutateAsync(values.authCode)
        .then(async () => {
          onClose()
          setTfaEnabled(false)
          onSuccess()
          toast.success('Two-factor authentication disabled successfully.')
        })
        .catch((error) => {
          const errorMessage = error?.response?.data?.detail

          if (!errorMessage) return

          form.setError('authCode', {
            type: 'manual',
            message: errorMessage,
          })
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disableTfaMutation, onSuccess],
  )

  const handleCancel = useCallback(() => {
    form.reset({ authCode: '' })
    form.clearErrors(['authCode'])
  }, [form])

  const handleClose = useCallback(() => {
    onClose()
    handleCancel()
  }, [onClose, handleCancel])

  return (
    <FormProvider {...form}>
      <Dialog
        show={show}
        onClose={onClose}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Dialog.Header>
            <Dialog.Title>Disable Two-Factor Authentication</Dialog.Title>
          </Dialog.Header>
          <Dialog.Description>
            <p className="mt-4 text-center">
              Enter the authentication code or backup code to disable two-factor
              authentication.
            </p>
          </Dialog.Description>
          <div className="flex flex-col gap-4">
            <FormField
              name="authCode"
              label="Authenticator Code"
              placeholder="Enter code"
            />
          </div>
          <Dialog.ClosableFooter onClose={handleClose}>
            <Button type="submit">Deactivate</Button>
          </Dialog.ClosableFooter>
        </form>
      </Dialog>
    </FormProvider>
  )
}
